import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Building2, PackagePlus, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];

const statusStyles = {
  pending: "bg-secondary/15 text-secondary-foreground border border-secondary/50",
  confirmed: "bg-accent/10 text-accent border border-accent/40",
  shipped: "bg-primary/10 text-primary border border-primary/30",
  delivered: "bg-primary text-white border border-primary",
  cancelled: "bg-destructive/10 text-destructive border border-destructive/40",
};

const StatusBadge = ({ status }) => (
  <span
    className={`font-monotech text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full ${statusStyles[status] || statusStyles.pending}`}
  >
    {status}
  </span>
);

export default function BulkOrders() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && user.role === "admin";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    organization: "",
    contact_phone: "",
    product: "",
    quantity: "",
    notes: "",
  });

  const fetchOrders = useCallback(async (admin) => {
    try {
      const { data } = await api.get(admin ? "/bulk-orders/all" : "/bulk-orders");
      setOrders(data);
    } catch (err) {
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
      } else {
        toast.error("Could not load orders. Please refresh.");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (user === false) navigate("/login");
    if (user) fetchOrders(user.role === "admin");
  }, [user, navigate, fetchOrders]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product) {
      toast.error("Please select a product");
      return;
    }
    setLoading(true);
    try {
      await api.post("/bulk-orders", { ...form, quantity: Number(form.quantity) });
      toast.success("Bulk order placed! Our B2B team will confirm shortly.");
      setForm({ organization: "", contact_phone: "", product: "", quantity: "", notes: "" });
      fetchOrders(isAdmin);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await api.patch(`/bulk-orders/${orderId}/status`, { status });
      toast.success(`Order marked ${status}`);
      fetchOrders(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (!user) return <div className="min-h-screen bg-background" data-testid="bulk-orders-loading" />;

  return (
    <div className="min-h-screen bg-background" data-testid="bulk-orders-page">
      <header className="bg-primary text-white">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" data-testid="bulk-back-home" className="flex items-center gap-2 text-sm text-white/80 hover:text-secondary transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to site
          </Link>
          <span className="font-heading font-extrabold text-lg tracking-tighter">
            Heal<span className="text-secondary">seeal</span>
            <span className="font-monotech text-[10px] tracking-[0.25em] uppercase text-secondary ml-3 hidden sm:inline">
              B2B Portal
            </span>
          </span>
          <button
            onClick={logout}
            data-testid="bulk-logout-btn"
            className="flex items-center gap-1.5 text-sm text-white/80 hover:text-secondary transition-colors"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 md:py-20">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="font-monotech text-xs tracking-[0.25em] uppercase text-secondary-foreground/70">
            For Hospitals · Clinics · Pharmacies
          </span>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-primary mt-3">
            Bulk order portal
          </h1>
          <p className="text-text-muted text-base md:text-lg mt-4 max-w-xl">
            {isAdmin
              ? "Admin view — review incoming B2B orders and update their fulfilment status."
              : `Welcome, ${user.name}. Place volume orders and track their status below.`}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mt-12">
          {!isAdmin && (
            <motion.form
              onSubmit={submit}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-2 bg-white border border-border rounded-3xl p-7 md:p-8 h-fit"
              data-testid="bulk-order-form"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-2xl bg-primary grid place-items-center">
                  <PackagePlus className="w-5 h-5 text-white" />
                </span>
                <h2 className="font-heading font-bold text-xl text-primary">New bulk order</h2>
              </div>
              <div className="space-y-5">
                <div>
                  <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">
                    Organization
                  </label>
                  <Input
                    required
                    value={form.organization}
                    onChange={(e) => setForm({ ...form, organization: e.target.value })}
                    placeholder="Hospital / Clinic / Pharmacy name"
                    data-testid="bulk-form-organization"
                    className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0"
                  />
                </div>
                <div>
                  <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">
                    Contact Phone
                  </label>
                  <Input
                    required
                    value={form.contact_phone}
                    onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                    placeholder="+91 …"
                    data-testid="bulk-form-phone"
                    className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">
                      Product
                    </label>
                    <Select value={form.product} onValueChange={(v) => setForm({ ...form, product: v })}>
                      <SelectTrigger data-testid="bulk-form-product" className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus:ring-0 px-0">
                        <SelectValue placeholder="Select…" />
                      </SelectTrigger>
                      <SelectContent className="bg-white">
                        <SelectItem value="Healseeal Jar (1000g)">Healseeal Jar (1000g) — ₹2,212</SelectItem>
                        <SelectItem value="Healseeal Tube (50g)">Healseeal Tube (50g) — On Enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">
                      Quantity
                    </label>
                    <Input
                      required
                      type="number"
                      min={1}
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      placeholder="e.g. 50"
                      data-testid="bulk-form-quantity"
                      className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-monotech text-xs tracking-widest uppercase text-text-muted">
                    Notes (optional)
                  </label>
                  <Textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    placeholder="Delivery timeline, GST details…"
                    data-testid="bulk-form-notes"
                    className="mt-2 rounded-none border-x-0 border-t-0 border-b-2 focus-visible:ring-0 focus-visible:border-primary px-0 resize-none"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="bulk-form-submit"
                  className="w-full rounded-none bg-primary text-white hover:bg-accent h-12 text-sm font-semibold tracking-wide"
                >
                  {loading ? "Placing order…" : "Place Bulk Order"}
                </Button>
              </div>
            </motion.form>
          )}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className={isAdmin ? "lg:col-span-5" : "lg:col-span-3"}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="w-10 h-10 rounded-2xl bg-secondary grid place-items-center">
                <Building2 className="w-5 h-5 text-primary" />
              </span>
              <h2 className="font-heading font-bold text-xl text-primary">
                {isAdmin ? "All B2B orders" : "Your orders"}
              </h2>
            </div>

            {orders.length === 0 ? (
              <div
                className="border border-dashed border-border rounded-3xl p-10 text-center text-text-muted"
                data-testid="bulk-orders-empty"
              >
                No bulk orders yet{isAdmin ? "." : " — place your first order to get started."}
              </div>
            ) : (
              <div className="space-y-3" data-testid="bulk-orders-list">
                {orders.map((o, i) => (
                  <div
                    key={o.id}
                    className="bg-white border border-border rounded-2xl p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4"
                    data-testid={`bulk-order-row-${i}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-monotech text-xs font-bold text-primary">{o.order_number}</span>
                        <StatusBadge status={o.status} />
                      </div>
                      <div className="font-heading font-bold text-primary mt-2">
                        {o.product} <span className="text-secondary-foreground/70">× {o.quantity}</span>
                      </div>
                      <div className="text-xs text-text-muted mt-1">
                        {o.organization} · {o.contact_phone}
                        {isAdmin && ` · ${o.user_name} (${o.user_email})`}
                      </div>
                      {o.notes && <div className="text-xs text-text-muted mt-1 italic">“{o.notes}”</div>}
                      <div className="font-monotech text-[10px] text-text-muted mt-2">
                        {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="shrink-0">
                        <Select value={o.status} onValueChange={(v) => updateStatus(o.id, v)}>
                          <SelectTrigger
                            data-testid={`admin-status-select-${i}`}
                            className="w-[150px] rounded-full border-border"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            {STATUSES.map((s) => (
                              <SelectItem key={s} value={s}>
                                {s.charAt(0).toUpperCase() + s.slice(1)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </main>
    </div>
  );
}
